import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Lock, Share2, FolderOpen, Key, Copy, Check, Bookmark, BookmarkCheck, ArrowBigUp, ArrowBigDown } from 'lucide-react';
import { WhatsappShareButton, TelegramShareButton, TwitterShareButton, RedditShareButton } from 'react-share';
import { FaWhatsapp, FaTelegram, FaXTwitter, FaReddit } from 'react-icons/fa6';
import { toast } from 'sonner';
import { getDisplayAuthor, getAuthorStyle, getAuthorAvatarColor, shouldShowPremiumBadge, getAuthorContainerClass } from '../utils/authorDisplay';
import PremiumBadge from './PremiumBadge';

const PostCard = ({ post, onUnlock, onVote, onSave, onUnsave, user }) => {
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [localSaved, setLocalSaved] = useState(post.is_saved || false);
  const [localVote, setLocalVote] = useState(post.user_vote || null);
  const [localUpvotes, setLocalUpvotes] = useState(post.upvotes || 0);
  const [localDownvotes, setLocalDownvotes] = useState(post.downvotes || 0);
  const [voteAnimation, setVoteAnimation] = useState({ upvote: false, downvote: false });
  
  // Get display author and styling
  const displayAuthor = getDisplayAuthor(post);
  const authorStyle = getAuthorStyle(post);
  const avatarColor = getAuthorAvatarColor(post);
  
  const shareUrl = window.location.origin;
  const shareText = `Check out this post on Ebonythroatqueens`;

  const handleKeyClick = () => {
    const isAdminOrPremium = user && (
      user.role === 'admin' || 
      user.role === 'super_admin' || 
      user.is_premium
    );

    if (isAdminOrPremium) {
      if (post.premium_key_link) {
        window.open(post.premium_key_link, '_blank');
      } else {
        toast.error('No key link available for this post');
      }
    } else {
      onUnlock(post);
    }
  };

  const copyKeyToClipboard = async () => {
    if (post.premium_key_link) {
      try {
        await navigator.clipboard.writeText(post.premium_key_link);
        setCopied(true);
        toast.success('Key link copied to clipboard!');
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        toast.error('Failed to copy');
      }
    }
  };

  const handleVote = async (voteType) => {
    // Trigger animation
    setVoteAnimation(prev => ({ ...prev, [voteType]: true }));
    setTimeout(() => {
      setVoteAnimation(prev => ({ ...prev, [voteType]: false }));
    }, 200);

    try {
      // Optimistic update
      if (localVote === voteType) {
        // Remove vote
        setLocalVote(null);
        if (voteType === 'upvote') {
          setLocalUpvotes(prev => Math.max(0, prev - 1));
        } else {
          setLocalDownvotes(prev => Math.max(0, prev - 1));
        }
        await onVote(post.post_id, null);
      } else {
        // Change or add vote
        const oldVote = localVote;
        setLocalVote(voteType);
        
        if (oldVote === 'upvote') {
          setLocalUpvotes(prev => Math.max(0, prev - 1));
          setLocalDownvotes(prev => prev + 1);
        } else if (oldVote === 'downvote') {
          setLocalDownvotes(prev => Math.max(0, prev - 1));
          setLocalUpvotes(prev => prev + 1);
        } else {
          if (voteType === 'upvote') {
            setLocalUpvotes(prev => prev + 1);
          } else {
            setLocalDownvotes(prev => prev + 1);
          }
        }
        
        await onVote(post.post_id, voteType);
      }
    } catch (error) {
      toast.error('Failed to vote');
      // Revert on error
      setLocalVote(post.user_vote || null);
      setLocalUpvotes(post.upvotes || 0);
      setLocalDownvotes(post.downvotes || 0);
    }
  };

  const handleSave = async () => {
    try {
      if (localSaved) {
        setLocalSaved(false);
        await onUnsave(post.post_id);
        toast.success('Post removed from saved');
      } else {
        setLocalSaved(true);
        await onSave(post.post_id);
        toast.success('Post saved');
      }
    } catch (error) {
      toast.error('Failed to save post');
      setLocalSaved(post.is_saved || false);
    }
  };

  return (
    <div
      className="post-card rounded-xl overflow-hidden transition-transform duration-200 hover:translate-y-[-3px] active:scale-[0.98] bg-[#121212] dark:bg-[#121212] light:bg-white border border-[#2A2A2A] dark:border-[#2A2A2A] light:border-gray-200"
      data-testid={`post-card-${post.post_id}`}
    >
      {/* Header */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-full flex items-center justify-center" 
            style={{ background: avatarColor }}
          >
            <span className="text-white font-bold text-sm">{displayAuthor[0]}</span>
          </div>
          <div>
            <div className={getAuthorContainerClass(post)}>
              <span 
                className="text-sm font-semibold" 
                style={{
                  color: authorStyle.color,
                  fontWeight: '600',
                  textTransform: authorStyle.textTransform,
                  fontFamily: authorStyle.fontFamily,
                  letterSpacing: authorStyle.letterSpacing,
                  textShadow: authorStyle.textShadow
                }}
                data-testid={`post-author-${post.post_id}`}
              >
                {displayAuthor}
              </span>
              {shouldShowPremiumBadge(post) && <PremiumBadge size={14} />}
            </div>
            <p className="text-gray-500 text-xs">
              {new Date(post.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        <span
          className="px-2 py-0.5 rounded-full text-xs font-bold flex items-center gap-1"
          style={{
            background: post.post_type === 'premium' ? '#EE1C4E' : '#2A2A2A',
            color: 'white',
            fontSize: '10px'
          }}
          data-testid={`post-type-${post.post_id}`}
        >
          {post.post_type === 'premium' ? '👑' : 'Free'}
        </span>
      </div>

      {/* Content */}
      <div className="relative">
        {post.image_url && (
          <img
            src={post.image_url}
            alt={post.title}
            className="w-full h-64 object-cover"
            data-testid={`post-image-${post.post_id}`}
          />
        )}
        
        <div className="p-4">
          <h2 className="text-white dark:text-white light:text-gray-900 font-bold text-xl mb-2" data-testid={`post-title-${post.post_id}`}>{post.title}</h2>
          <p className="text-gray-400 dark:text-gray-400 light:text-gray-600 text-sm leading-relaxed mb-4" data-testid={`post-text-${post.post_id}`}>{post.text}</p>
          
          {/* Action Buttons - Mega Folder and Key */}
          <div className="flex gap-3">
            {post.mega_folder_link && (
              <a
                href={post.mega_folder_link}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold transition-all hover:brightness-110 active:scale-[0.98]"
                style={{ background: '#2A2A2A', color: 'white' }}
                data-testid={`mega-folder-button-${post.post_id}`}
              >
                <FolderOpen className="w-4 h-4" />
                {post.feed_id === 'eliteebony' ? 'Get PPV Pack' : 'MEGA'}
              </a>
            )}
            
            {(post.linkvertise_url || post.premium_key_link) && (
              <div className="flex-1 flex gap-2">
                <button
                  onClick={handleKeyClick}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-bold transition-all hover:brightness-110 active:scale-[0.98] relative"
                  style={{ background: '#EE1C4E', color: 'white' }}
                  data-testid={`key-button-${post.post_id}`}
                >
                  <Key className="w-4 h-4" />
                  <span>Key</span>
                </button>
                
                {user && (user.role === 'admin' || user.role === 'super_admin' || user.is_premium) && post.premium_key_link && (
                  <button
                    onClick={copyKeyToClipboard}
                    className="px-3 py-3 rounded-lg text-sm font-bold transition-all hover:brightness-110 active:scale-[0.98]"
                    style={{ background: copied ? '#00C853' : '#2A2A2A', color: 'white' }}
                    title="Copy key link"
                    data-testid={`copy-key-button-${post.post_id}`}
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Voting, Save & Share */}
      <div className="p-4 border-t" style={{ borderColor: '#2A2A2A' }}>
        <div className="flex items-center justify-between">
          {/* Voting */}
          <div className="flex items-center gap-2" data-testid={`voting-${post.post_id}`}>
            <button
              onClick={() => handleVote('upvote')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all hover:bg-white/5 active:scale-[0.97]"
              style={{
                background: localVote === 'upvote' ? '#00C853' : 'transparent',
                color: localVote === 'upvote' ? 'white' : '#A1A1A1',
                transform: voteAnimation.upvote ? 'scale(1.1)' : 'scale(1)',
                transition: 'all 0.15s ease-out'
              }}
              data-testid={`upvote-button-${post.post_id}`}
              title="Upvote"
            >
              <ArrowBigUp className="w-5 h-5" fill={localVote === 'upvote' ? 'white' : 'none'} />
              <span 
                className="text-sm font-semibold"
                style={{
                  display: 'inline-block',
                  transform: voteAnimation.upvote ? 'scale(1.2)' : 'scale(1)',
                  transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
              >
                {localUpvotes}
              </span>
            </button>
            
            <button
              onClick={() => handleVote('downvote')}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg transition-all hover:bg-white/5 active:scale-[0.97]"
              style={{
                background: localVote === 'downvote' ? '#EE1C4E' : 'transparent',
                color: localVote === 'downvote' ? 'white' : '#A1A1A1',
                transform: voteAnimation.downvote ? 'scale(1.1)' : 'scale(1)',
                transition: 'all 0.15s ease-out'
              }}
              data-testid={`downvote-button-${post.post_id}`}
              title="Downvote"
            >
              <ArrowBigDown className="w-5 h-5" fill={localVote === 'downvote' ? 'white' : 'none'} />
              <span 
                className="text-sm font-semibold"
                style={{
                  display: 'inline-block',
                  transform: voteAnimation.downvote ? 'scale(1.2)' : 'scale(1)',
                  transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)'
                }}
              >
                {localDownvotes}
              </span>
            </button>
          </div>
          
          {/* Save & Share */}
          <div className="flex items-center gap-2">
            {/* Save Button */}
            <button
              onClick={handleSave}
              className="p-2 rounded-lg transition-all hover:bg-white/5 active:scale-[0.97]"
              data-testid={`save-button-${post.post_id}`}
              title={localSaved ? 'Unsave post' : 'Save post'}
            >
              {localSaved ? (
                <BookmarkCheck className="w-5 h-5 text-[#EE1C4E]" fill="#EE1C4E" />
              ) : (
                <Bookmark className="w-5 h-5 text-gray-400" />
              )}
            </button>
            
            {/* Share Button */}
            <div className="relative">
              <Button
                onClick={() => setShowShare(!showShare)}
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white p-2"
                data-testid={`share-button-${post.post_id}`}
              >
                <Share2 className="w-5 h-5" />
              </Button>
              
              {showShare && (
                <div
                  className="absolute right-0 bottom-full mb-2 p-3 rounded-lg flex gap-3"
                  style={{ background: '#1A1A1A', border: '1px solid #2A2A2A', zIndex: 10 }}
                  data-testid={`share-menu-${post.post_id}`}
                >
                  <WhatsappShareButton url={shareUrl} title={shareText}>
                    <FaWhatsapp className="w-6 h-6 text-green-500 hover:scale-110 transition-transform cursor-pointer" />
                  </WhatsappShareButton>
                  
                  <TelegramShareButton url={shareUrl} title={shareText}>
                    <FaTelegram className="w-6 h-6 text-blue-500 hover:scale-110 transition-transform cursor-pointer" />
                  </TelegramShareButton>
                  
                  <TwitterShareButton url={shareUrl} title={shareText}>
                    <FaXTwitter className="w-6 h-6 text-white hover:scale-110 transition-transform cursor-pointer" />
                  </TwitterShareButton>
                  
                  <RedditShareButton url={shareUrl} title={shareText}>
                    <FaReddit className="w-6 h-6 text-orange-500 hover:scale-110 transition-transform cursor-pointer" />
                  </RedditShareButton>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostCard;
